// AIBL API Service - Connected to https://api.aibl.dev
const API_BASE = 'https://api.aibl.dev';

// Simple in-memory cache for credit optimization
const cache = {
  account: null,
  accountTimestamp: 0,
  CACHE_TTL: 5 * 60 * 1000, // 5 minutes
};

// Error messages for known error codes
export const ERROR_MESSAGES = {
  // Auth errors
  SIGNATURE_REQUIRED: 'Wallet login requires a signature. Please sign the message.',
  INVALID_NONCE: 'Login session expired. Please try again.',
  NONCE_EXPIRED: 'Login session expired. Please try again.',
  INVALID_SIGNATURE: 'Signature verification failed. Please try again.',
  RATE_LIMITED: 'Too many requests. Please wait a moment.',
  
  // Agent errors
  PLUGIN_NOT_ENABLED: 'This plugin is not enabled for the agent.',
  AGENT_LIMIT_REACHED: 'Maximum agents reached for your tier.',
  INVALID_API_KEY: 'Invalid API key.',
  AGENT_INACTIVE: 'Agent is paused.',
  KEY_NOT_FOUND: 'API key not found for this agent.',
  
  // Safe errors
  SAFE_EXISTS: 'Agent already has a Safe on this chain.',
  INSUFFICIENT_FUNDS: 'Agent wallet needs ETH for gas. Fund the Turnkey EOA first.',
  
  // Withdrawal errors (v8.1)
  CONFIRMATION_REQUIRED: 'Please type CONFIRM-DELETE to permanently delete this agent.',
  INSUFFICIENT_BALANCE: 'Not enough balance for this withdrawal.',
  ZERO_AMOUNT: 'Enter an amount greater than zero.',
  SAFE_WITHDRAW_NOT_ALLOWED: 'Cannot withdraw directly from a Gnosis Safe. Switch to Smart Account first, or use the Safe propose flow.',
  WITHDRAW_FAILED: 'Withdrawal transaction failed. Please try again.',
  NO_WALLET: 'No wallet found on this chain.',
};

// Helper to get user-friendly error message
export const getErrorMessage = (error) => {
  if (!error) return 'An unknown error occurred.';
  
  // Check for known error codes
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  // Check for rate limit error
  if (error.code === 'RATE_LIMITED') {
    return ERROR_MESSAGES.RATE_LIMITED;
  }
  
  // Check for insufficient funds in message
  if (error.message?.toLowerCase().includes('insufficient funds')) {
    return ERROR_MESSAGES.INSUFFICIENT_FUNDS;
  }
  
  // Return the message or a default
  return error.message || 'An error occurred. Please try again.';
};

// Helper to get auth headers
const getHeaders = (token) => ({
  'Content-Type': 'application/json',
  ...(token ? { 'Authorization': `Bearer ${token}` } : {})
});

// Helper for API key auth (for agent operations)
const getApiKeyHeaders = (apiKey) => ({
  'Content-Type': 'application/json',
  'x-api-key': apiKey
});

// Helper for account key auth
const getAccountKeyHeaders = (accountKey) => ({
  'Content-Type': 'application/json',
  'x-account-key': accountKey
});

const api = {
  // ==================== AUTH ====================
  
  // Get nonce for wallet signature
  getNonce: async () => {
    const res = await fetch(`${API_BASE}/v1/auth/nonce`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    return res.json();
  },

  // Google login
  googleLogin: async (idToken) => {
    const res = await fetch(`${API_BASE}/v1/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken })
    });
    return res.json();
  },

  // Email login
  emailLogin: async (email) => {
    const res = await fetch(`${API_BASE}/v1/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    return res.json();
  },

  // Login with wallet address
  login: async (walletAddress, signature, nonce) => {
    const res = await fetch(`${API_BASE}/v1/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ walletAddress, signature, nonce })
    });
    return res.json();
  },

  // Get current user + their agents
  getMe: async (token) => {
    const res = await fetch(`${API_BASE}/v1/auth/me`, {
      headers: getHeaders(token)
    });
    return res.json();
  },

  // ==================== ACCOUNT (v6) ====================

  // Get account details (CACHE THIS!)
  getAccount: async (token, forceRefresh = false) => {
    // Check cache first
    if (!forceRefresh && cache.account && (Date.now() - cache.accountTimestamp < cache.CACHE_TTL)) {
      return { success: true, data: cache.account, cached: true };
    }
    const res = await fetch(`${API_BASE}/v1/account`, {
      headers: getHeaders(token)
    });
    const data = await res.json();
    if (data.success) {
      cache.account = data.data;
      cache.accountTimestamp = Date.now();
    }
    return data;
  },

  // Invalidate account cache (call after mutations)
  invalidateAccountCache: () => {
    cache.account = null;
    cache.accountTimestamp = 0;
  },

  // Link wallet to account
  linkWallet: async (token, walletAddress) => {
    api.invalidateAccountCache();
    const res = await fetch(`${API_BASE}/v1/account/link-wallet`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ walletAddress })
    });
    return res.json();
  },

  // Link Google to account
  linkGoogle: async (token, idToken) => {
    api.invalidateAccountCache();
    const res = await fetch(`${API_BASE}/v1/account/link-google`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ idToken })
    });
    return res.json();
  },

  // Update account (display name, etc.)
  updateAccount: async (token, data) => {
    api.invalidateAccountCache();
    const res = await fetch(`${API_BASE}/v1/account`, {
      method: 'PATCH',
      headers: getHeaders(token),
      body: JSON.stringify(data)
    });
    return res.json();
  },

  // Generate account API key
  generateAccountApiKey: async (token) => {
    const res = await fetch(`${API_BASE}/v1/account/api-key`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    return res.json();
  },

  // Get account API key info
  getAccountApiKeyInfo: async (token) => {
    const res = await fetch(`${API_BASE}/v1/account/api-key/info`, {
      headers: getHeaders(token)
    });
    return res.json();
  },

  // Revoke account API key
  revokeAccountApiKey: async (token) => {
    const res = await fetch(`${API_BASE}/v1/account/api-key`, {
      method: 'DELETE',
      headers: getHeaders(token)
    });
    return res.json();
  },

  // ==================== AGENTS ====================

  // List user's agents
  getAgents: async (token) => {
    const res = await fetch(`${API_BASE}/v1/agents`, {
      headers: getHeaders(token)
    });
    return res.json();
  },

  // Get single agent
  getAgent: async (token, agentId) => {
    const res = await fetch(`${API_BASE}/v1/agents/${agentId}`, {
      headers: getHeaders(token)
    });
    return res.json();
  },

  // Update agent
  updateAgent: async (token, agentId, data) => {
    const res = await fetch(`${API_BASE}/v1/agents/${agentId}`, {
      method: 'PATCH',
      headers: getHeaders(token),
      body: JSON.stringify(data)
    });
    return res.json();
  },

  // Delete agent (v8.1 - requires CONFIRM-DELETE phrase)
  deleteAgent: async (token, agentId, confirmPhrase) => {
    const res = await fetch(`${API_BASE}/v1/agents/${agentId}`, {
      method: 'DELETE',
      headers: getHeaders(token),
      body: JSON.stringify({ confirmPhrase })
    });
    return res.json();
  },

  // ==================== WITHDRAWALS (v8.1) ====================

  // Preview withdrawal (check balance)
  previewWithdrawal: async (token, agentId, chain, tokenAddress = null) => {
    const url = tokenAddress 
      ? `${API_BASE}/v1/agents/${agentId}/withdraw/preview?chain=${chain}&token=${tokenAddress}`
      : `${API_BASE}/v1/agents/${agentId}/withdraw/preview?chain=${chain}`;
    const res = await fetch(url, {
      headers: getHeaders(token)
    });
    return res.json();
  },

  // Execute withdrawal
  executeWithdrawal: async (token, agentId, { toAddress, chain, token: tokenAddress, amount }) => {
    const body = { toAddress, chain, amount };
    if (tokenAddress) body.token = tokenAddress;
    
    const res = await fetch(`${API_BASE}/v1/agents/${agentId}/withdraw`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify(body)
    });
    return res.json();
  },

  // Add wallet to agent on new chain
  addWallet: async (token, agentId, chain) => {
    const res = await fetch(`${API_BASE}/v1/agents/${agentId}/wallets`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ chain })
    });
    return res.json();
  },

  // Create agent (returns agent + wallet + API key)
  createAgent: async (token, agentData) => {
    const res = await fetch(`${API_BASE}/v1/agents`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify(agentData)
    });
    return res.json();
  },

  // ==================== WALLETS ====================

  // Get agent's wallets
  getAgentWallets: async (token, agentId) => {
    const res = await fetch(`${API_BASE}/v1/wallets/${agentId}`, {
      headers: getHeaders(token)
    });
    return res.json();
  },

  // Get wallet balance
  getWalletBalance: async (token, address, chain = 'base') => {
    const res = await fetch(`${API_BASE}/v1/wallets/${address}/balance?chain=${chain}`, {
      headers: getHeaders(token)
    });
    return res.json();
  },

  // ==================== GNOSIS SAFE ====================

  // Deploy a Gnosis Safe
  deploySafe: async (token, agentId, chain, threshold, signers) => {
    const res = await fetch(`${API_BASE}/v1/safe/deploy`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ agentId, chain, threshold, signers })
    });
    return res.json();
  },

  // Import existing Safe
  importSafe: async (token, { safeAddress, chain, label }) => {
    const res = await fetch(`${API_BASE}/v1/safe/import`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ safeAddress, chain, label })
    });
    return res.json();
  },

  // Auto-discover Safes for agent
  discoverSafes: async (token, agentId, chain = 'base', autoImport = true) => {
    const res = await fetch(`${API_BASE}/v1/safe/discover/${agentId}`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ chain, autoImport })
    });
    return res.json();
  },

  // Get Safe info for agent
  getSafeInfo: async (token, agentId) => {
    const res = await fetch(`${API_BASE}/v1/safe/${agentId}`, {
      headers: getHeaders(token)
    });
    return res.json();
  },

  // Switch primary wallet (SimpleAccount <-> Gnosis Safe)
  switchPrimaryWallet: async (token, agentId, walletId) => {
    const res = await fetch(`${API_BASE}/v1/safe/${agentId}/switch`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ walletId })
    });
    return res.json();
  },

  // Add signer to Safe (proposes tx)
  addSafeSigner: async (token, agentId, { address, label, newThreshold }) => {
    const res = await fetch(`${API_BASE}/v1/safe/${agentId}/signers/add`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ address, label, newThreshold })
    });
    return res.json();
  },

  // Remove signer from Safe (proposes tx)
  removeSafeSigner: async (token, agentId, address) => {
    const res = await fetch(`${API_BASE}/v1/safe/${agentId}/signers/remove`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ address })
    });
    return res.json();
  },

  // Change Safe threshold (proposes tx)
  changeSafeThreshold: async (token, agentId, threshold) => {
    const res = await fetch(`${API_BASE}/v1/safe/${agentId}/threshold`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ threshold })
    });
    return res.json();
  },

  // Get pending Safe transactions (v8 endpoint)
  getSafePendingTxs: async (token, agentId) => {
    const res = await fetch(`${API_BASE}/v1/safe/${agentId}/pending`, {
      headers: getHeaders(token)
    });
    return res.json();
  },

  // Get Safe transaction history
  getSafeTransactions: async (token, agentId, status = 'EXECUTED', limit = 50) => {
    const res = await fetch(`${API_BASE}/v1/safe/${agentId}/transactions?status=${status}&limit=${limit}`, {
      headers: getHeaders(token)
    });
    return res.json();
  },

  // Propose a Safe transaction (v8 new endpoint)
  proposeSafeTx: async (token, agentId, { to, value, data, plugin, description }) => {
    const res = await fetch(`${API_BASE}/v1/safe/${agentId}/propose`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ to, value, data: data || '0x', plugin, description })
    });
    return res.json();
  },

  // Confirm Safe transaction (v8 - signature required)
  confirmSafeTx: async (token, safeTxId, signerAddress, signature) => {
    const res = await fetch(`${API_BASE}/v1/safe/confirm/${safeTxId}`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ signerAddress, signature })
    });
    return res.json();
  },

  // Reject Safe transaction
  rejectSafeTx: async (token, safeTxId, signerAddress, reason) => {
    const res = await fetch(`${API_BASE}/v1/safe/reject/${safeTxId}`, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ signerAddress, reason })
    });
    return res.json();
  },

  // Execute Safe transaction (after threshold met)
  executeSafeTx: async (token, safeTxId) => {
    const res = await fetch(`${API_BASE}/v1/safe/execute/${safeTxId}`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    return res.json();
  },

  // Update Safe config
  updateSafeConfig: async (token, agentId, walletId, data) => {
    const res = await fetch(`${API_BASE}/v1/safe/${agentId}/${walletId}`, {
      method: 'PATCH',
      headers: getHeaders(token),
      body: JSON.stringify(data)
    });
    return res.json();
  },

  // ==================== PLUGINS ====================

  // List all plugins
  getPlugins: async () => {
    const res = await fetch(`${API_BASE}/v1/plugins`);
    return res.json();
  },

  // Get agent's plugin states
  getAgentPlugins: async (token, agentId) => {
    const res = await fetch(`${API_BASE}/v1/agents/${agentId}/plugins`, {
      headers: getHeaders(token)
    });
    return res.json();
  },

  // Update single plugin config for agent
  updatePlugin: async (token, agentId, pluginName, data) => {
    const res = await fetch(`${API_BASE}/v1/agents/${agentId}/plugins/${pluginName}`, {
      method: 'PATCH',
      headers: getHeaders(token),
      body: JSON.stringify(data)
    });
    return res.json();
  },

  // Bulk update plugins
  bulkUpdatePlugins: async (token, agentId, plugins) => {
    const res = await fetch(`${API_BASE}/v1/agents/${agentId}/plugins`, {
      method: 'PUT',
      headers: getHeaders(token),
      body: JSON.stringify({ plugins })
    });
    return res.json();
  },

  // Get swap quote
  getSwapQuote: async (apiKey, tokenIn, tokenOut, amount, chain = 'base') => {
    const res = await fetch(`${API_BASE}/v1/plugins/swap/quote`, {
      method: 'POST',
      headers: getApiKeyHeaders(apiKey),
      body: JSON.stringify({ tokenIn, tokenOut, amount, chain })
    });
    return res.json();
  },

  // Execute swap
  executeSwap: async (apiKey, tokenIn, tokenOut, amount, chain = 'base') => {
    const res = await fetch(`${API_BASE}/v1/plugins/swap/execute`, {
      method: 'POST',
      headers: getApiKeyHeaders(apiKey),
      body: JSON.stringify({ tokenIn, tokenOut, amount, chain })
    });
    return res.json();
  },

  // Execute transfer
  executeTransfer: async (apiKey, to, amount, token = 'ETH', chain = 'base') => {
    const res = await fetch(`${API_BASE}/v1/plugins/transfer/execute`, {
      method: 'POST',
      headers: getApiKeyHeaders(apiKey),
      body: JSON.stringify({ to, amount, token, chain })
    });
    return res.json();
  },

  // Execute wrap/unwrap
  executeWrap: async (apiKey, action, amount, chain = 'base') => {
    const res = await fetch(`${API_BASE}/v1/plugins/wrap/execute`, {
      method: 'POST',
      headers: getApiKeyHeaders(apiKey),
      body: JSON.stringify({ action, amount, chain })
    });
    return res.json();
  },

  // Execute bridge
  executeBridge: async (apiKey, fromChain, toChain, token, amount) => {
    const res = await fetch(`${API_BASE}/v1/plugins/bridge/execute`, {
      method: 'POST',
      headers: getApiKeyHeaders(apiKey),
      body: JSON.stringify({ fromChain, toChain, token, amount })
    });
    return res.json();
  },

  // ==================== DASHBOARD ====================

  getDashboardOverview: async (token) => {
    const res = await fetch(`${API_BASE}/v1/dashboard/overview`, {
      headers: getHeaders(token)
    });
    return res.json();
  },

  getDashboardActivity: async (token) => {
    const res = await fetch(`${API_BASE}/v1/dashboard/activity`, {
      headers: getHeaders(token)
    });
    return res.json();
  },

  getTransactions: async (token) => {
    const res = await fetch(`${API_BASE}/v1/dashboard/transactions`, {
      headers: getHeaders(token)
    });
    return res.json();
  },

  // ==================== LEADERBOARD (Public) ====================

  getLeaderboard: async (limit = 50, offset = 0) => {
    const res = await fetch(`${API_BASE}/v1/leaderboard?limit=${limit}&offset=${offset}`);
    return res.json();
  },

  getLiveBalance: async (address, chain = 'base') => {
    const res = await fetch(`${API_BASE}/v1/leaderboard/live/${address}?chain=${chain}`);
    return res.json();
  },

  // ==================== SUPPORTED CHAINS ====================

  getSupportedChains: async () => {
    const res = await fetch(`${API_BASE}/v1/supported-chains`);
    return res.json();
  },

  // ==================== DEVELOPER SETTINGS ====================

  getProfile: async (token) => {
    const res = await fetch(`${API_BASE}/v1/developer/profile`, {
      headers: getHeaders(token)
    });
    return res.json();
  },

  regenerateApiKey: async (token, agentId) => {
    const res = await fetch(`${API_BASE}/v1/agents/${agentId}/regenerate-key`, {
      method: 'POST',
      headers: getHeaders(token)
    });
    return res.json();
  },
};

export default api;

// Chain configuration with logos (using reliable CDN URLs)
export const CHAINS = {
  base: { 
    key: 'base', 
    id: 8453, 
    name: 'Robinhood Network', 
    logo: '/robinhood-logo.png',
    explorer: 'https://basescan.org' 
  },
  ethereum: { 
    key: 'ethereum', 
    id: 1, 
    name: 'Ethereum', 
    logo: 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/eth.png',
    explorer: 'https://etherscan.io' 
  },
  arbitrum: { 
    key: 'arbitrum', 
    id: 42161, 
    name: 'Arbitrum', 
    logo: 'https://avatars.githubusercontent.com/u/56302961?s=200&v=4',
    explorer: 'https://arbiscan.io' 
  },
  optimism: { 
    key: 'optimism', 
    id: 10, 
    name: 'Optimism', 
    logo: 'https://avatars.githubusercontent.com/u/58791460?s=200&v=4',
    explorer: 'https://optimistic.etherscan.io' 
  },
  polygon: { 
    key: 'polygon', 
    id: 137, 
    name: 'Polygon', 
    logo: 'https://avatars.githubusercontent.com/u/30753617?s=200&v=4',
    explorer: 'https://polygonscan.com' 
  },
};

// Plugin configuration
export const PLUGINS = {
  // Enabled plugins
  swap: { id: 'swap', name: 'Swap', description: 'Exchange any token for another via 0x aggregator', enabled: true },
  transfer: { id: 'transfer', name: 'Transfer', description: 'Send ETH or tokens to any address', enabled: true },
  wrap: { id: 'wrap', name: 'Wrap/Unwrap', description: 'Wrap ETH → WETH or unwrap WETH → ETH', enabled: true },
  buy_tokens: { id: 'buy_tokens', name: 'Buy Tokens', description: 'Buy any token (routes through swap)', enabled: true },
  bridge: { id: 'bridge', name: 'Bridge', description: 'Move tokens across chains', enabled: true },
  
  // Coming soon plugins
  burn_tokens: { id: 'burn_tokens', name: 'Burn Tokens', description: 'Permanently remove tokens from supply', enabled: false },
  lock_vest: { id: 'lock_vest', name: 'Lock & Vest', description: 'Create token vesting schedules', enabled: false },
  add_liquidity: { id: 'add_liquidity', name: 'Add Liquidity', description: 'Provide liquidity to DEX pools', enabled: false },
  lock_lp: { id: 'lock_lp', name: 'Lock LP', description: 'Time-lock LP positions', enabled: false },
  burn_lp: { id: 'burn_lp', name: 'Burn LP', description: 'Permanently burn liquidity', enabled: false },
  yield_farming: { id: 'yield_farming', name: 'Yield Farming', description: 'Auto-compound across protocols', enabled: false },
  precious_metals: { id: 'precious_metals', name: 'Precious Metals', description: 'Buy tokenized gold/silver', enabled: false },
  lending: { id: 'lending', name: 'Lending', description: 'Supply to Aave/Compound, earn yield', enabled: false },
  arbitrage: { id: 'arbitrage', name: 'Arbitrage', description: 'Cross-DEX price arbitrage', enabled: false },
  multi_chain: { id: 'multi_chain', name: 'Multi-Chain', description: 'Execute across chains atomically', enabled: false },
  index_fund: { id: 'index_fund', name: 'Index Fund', description: 'Invest in token baskets', enabled: false },
  flash_loans: { id: 'flash_loans', name: 'Flash Loans', description: 'Borrow and repay in one tx', enabled: false },
};

// Token addresses on Robinhood Network
export const TOKENS = {
  ETH: { symbol: 'ETH', name: 'Ethereum', address: 'ETH', decimals: 18 },
  USDC: { symbol: 'USDC', name: 'USD Coin', address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', decimals: 6 },
  WETH: { symbol: 'WETH', name: 'Wrapped ETH', address: '0x4200000000000000000000000000000000000006', decimals: 18 },
  DAI: { symbol: 'DAI', name: 'Dai', address: '0x50c5725949A6F0c72E6C4a641F24049A917DB0Cb', decimals: 18 },
};

// Helper to get explorer links
export const getExplorerTxUrl = (chain, txHash) => {
  const chainConfig = CHAINS[chain];
  return chainConfig ? `${chainConfig.explorer}/tx/${txHash}` : `https://basescan.org/tx/${txHash}`;
};

export const getExplorerAddressUrl = (chain, address) => {
  const chainConfig = CHAINS[chain];
  return chainConfig ? `${chainConfig.explorer}/address/${address}` : `https://basescan.org/address/${address}`;
};

/**
 * Adjust wallet signature for Gnosis Safe format
 * Safe requires v=31/32 for eth_sign signatures (instead of 27/28)
 * This is CRITICAL for Safe transaction confirmations
 */
export const adjustSignatureForSafe = (signature) => {
  const bytes = new Uint8Array(
    signature.slice(2).match(/.{2}/g).map(b => parseInt(b, 16))
  );
  const v = bytes[64];
  if (v === 27) bytes[64] = 31;
  else if (v === 28) bytes[64] = 32;
  return '0x' + Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

// Common Robinhood Network tokens for withdrawal quick-select (v8.1)
export const ROBINHOOD_TOKENS = [
  { symbol: 'ETH', address: null, decimals: 18, name: 'Ethereum' },
  { symbol: 'USDC', address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', decimals: 6, name: 'USD Coin' },
  { symbol: 'USDbC', address: '0xd9aAEc86B65D86f6A7B5B1b0c42FFA531710b6CA', decimals: 6, name: 'USD Coin (Bridged)' },
  { symbol: 'WETH', address: '0x4200000000000000000000000000000000000006', decimals: 18, name: 'Wrapped Ether' },
  { symbol: 'DAI', address: '0x50c5725949A6F0c72E6C4a641F24049A917DB0Cb', decimals: 18, name: 'Dai Stablecoin' },
];


